import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Toolbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Tooltip
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as NumberedListIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  FormatAlignJustify as AlignJustifyIcon,
  Undo as UndoIcon,
  Redo as RedoIcon
} from '@mui/icons-material';

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  label = 'Rich Text Editor',
  placeholder = 'Enter text...',
  rows = 4,
  fullWidth = true,
  helperText,
  error,
  disabled = false
}) => {
  const editorRef = useRef(null);
  const [history, setHistory] = useState([value]);
  const [historyIndex, setHistoryIndex] = useState(0);

  // Initialize history with the initial value
  useEffect(() => {
    if (history.length === 1 && history[0] !== value) {
      setHistory([value]);
      setHistoryIndex(0);
    }
  }, []);

  // Only update content if it's different and we're not currently typing
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML && !editorRef.current.matches(':focus')) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const [currentFontSize, setCurrentFontSize] = useState('14');

  // Function to get current font size from selection
  const getCurrentFontSize = useCallback(() => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let container = range.commonAncestorContainer;
        
        // If it's a text node, get parent
        if (container.nodeType === Node.TEXT_NODE) {
          container = container.parentElement;
        }
        
        // Walk up the DOM tree to find explicit font size
        let currentElement = container;
        while (currentElement && currentElement !== editorRef.current) {
          // Check inline style first (most specific)
          if (currentElement.style && currentElement.style.fontSize) {
            const size = currentElement.style.fontSize.replace('px', '').trim();
            if (size && !isNaN(size)) {
              return size;
            }
          }
          
          // Check computed style if no inline style
          const computedFontSize = window.getComputedStyle(currentElement).fontSize;
          if (computedFontSize) {
            const size = parseFloat(computedFontSize);
            // Only return if it's not the default browser size (usually 16px)
            // and if it's explicitly set in a span
            if (currentElement.tagName === 'SPAN' && currentElement.style.fontSize) {
              return Math.round(size).toString();
            }
          }
          
          currentElement = currentElement.parentElement;
        }
      }
      
      // Default font size
      const defaultSize = window.getComputedStyle(editorRef.current).fontSize;
      if (defaultSize) {
        return Math.round(parseFloat(defaultSize)).toString();
      }
    }
    return '14'; // Fallback default
  }, []);

  // Update current font size when selection changes
  useEffect(() => {
    const handleSelectionChange = () => {
      if (editorRef.current && (editorRef.current.contains(document.activeElement) || editorRef.current === document.activeElement)) {
        const detectedSize = getCurrentFontSize();
        setCurrentFontSize(prev => {
          // Only update if different to avoid unnecessary re-renders
          return prev !== detectedSize ? detectedSize : prev;
        });
      }
    };

    // Also listen to click and keyup events for more reliable updates
    const handleEditorInteraction = () => {
      setTimeout(handleSelectionChange, 10);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    if (editorRef.current) {
      editorRef.current.addEventListener('click', handleEditorInteraction);
      editorRef.current.addEventListener('keyup', handleEditorInteraction);
    }

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (editorRef.current) {
        editorRef.current.removeEventListener('click', handleEditorInteraction);
        editorRef.current.removeEventListener('keyup', handleEditorInteraction);
      }
    };
  }, [getCurrentFontSize]);

  const executeCommand = (command, value = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command, false, value);
      updateContent();
    }
  };

  const updateContent = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
      
      // Add to history only if content actually changed
      if (content !== history[historyIndex]) {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(content);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }
    }
  }, [history, historyIndex, onChange]);

  // Debounced version for typing
  const debouncedUpdateContent = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      // Only update if content actually changed
      if (content !== value) {
        onChange(content);
      }
    }
  }, [onChange, value]);

  const handleKeyDown = (e) => {
    // Handle Ctrl+Z (undo) and Ctrl+Y (redo)
    if (e.ctrlKey) {
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    }
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      onChange(history[newIndex]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      onChange(history[newIndex]);
    }
  };

  const handleFontSizeChange = (event) => {
    const size = event.target.value;
    setCurrentFontSize(size);
    
    if (editorRef.current) {
      editorRef.current.focus();
      
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Helper function to remove existing font size spans
        const removeFontSizeSpans = (element) => {
          if (!element) return;
          
          // Find all spans with font-size style
          const spansWithFontSize = element.querySelectorAll('span[style*="font-size"]');
          spansWithFontSize.forEach(span => {
            const parent = span.parentNode;
            while (span.firstChild) {
              parent.insertBefore(span.firstChild, span);
            }
            parent.removeChild(span);
          });
        };
        
        if (!selection.isCollapsed) {
          // There's a text selection - wrap it in a span with the font size
          // First, check if selection is already wrapped in a font-size span
          let container = range.commonAncestorContainer;
          if (container.nodeType === Node.TEXT_NODE) {
            container = container.parentElement;
          }
          
          // Check if entire selection is within a single span with font size
          let existingSpan = null;
          if (container.tagName === 'SPAN' && container.style.fontSize) {
            // Check if the selection is entirely within this span
            const spanRange = document.createRange();
            spanRange.selectNodeContents(container);
            if (range.compareBoundaryPoints(Range.START_TO_START, spanRange) >= 0 &&
                range.compareBoundaryPoints(Range.END_TO_END, spanRange) <= 0) {
              existingSpan = container;
            }
          }
          
          if (existingSpan) {
            // Just update the existing span's font size
            existingSpan.style.fontSize = `${size}px`;
            // Restore selection
            selection.removeAllRanges();
            selection.addRange(range);
          } else {
            // Extract and wrap in new span
            const selectedContent = range.extractContents();
            removeFontSizeSpans(selectedContent);
            
            // Create new span with font size
            const span = document.createElement('span');
            span.style.fontSize = `${size}px`;
            span.appendChild(selectedContent);
            range.insertNode(span);
            
            // Restore selection to the newly wrapped content
            selection.removeAllRanges();
            const newRange = document.createRange();
            newRange.selectNodeContents(span);
            selection.addRange(newRange);
          }
        } else {
          // No selection - check if we're in a span with font size and update it
          let container = range.commonAncestorContainer;
          if (container.nodeType === Node.TEXT_NODE) {
            container = container.parentElement;
          }
          
          // Walk up to find a span with font size
          let fontSpan = null;
          let current = container;
          while (current && current !== editorRef.current) {
            if (current.tagName === 'SPAN' && current.style.fontSize) {
              fontSpan = current;
              break;
            }
            current = current.parentElement;
          }
          
          if (fontSpan) {
            // Update existing font size span
            fontSpan.style.fontSize = `${size}px`;
          } else {
            // Apply font size to the current block element (paragraph, div, etc.)
            // This is simpler and more reliable for cursor-only scenarios
            let blockElement = container;
            let fontSizeApplied = false;
            
            while (blockElement && blockElement !== editorRef.current) {
              if (blockElement.tagName === 'P' || blockElement.tagName === 'DIV') {
                blockElement.style.fontSize = `${size}px`;
                fontSizeApplied = true;
                break;
              }
              blockElement = blockElement.parentElement;
            }
            
            // If no block element found, create a span wrapper
            if (!fontSizeApplied) {
              // This will apply font size to future typing
              const span = document.createElement('span');
              span.style.fontSize = `${size}px`;
              const textNode = document.createTextNode('');
              span.appendChild(textNode);
              
              try {
                range.insertNode(span);
                const newRange = document.createRange();
                newRange.setStart(textNode, 0);
                newRange.setEnd(textNode, 0);
                selection.removeAllRanges();
                selection.addRange(newRange);
              } catch (e) {
                // Fallback
                try {
                  document.execCommand('insertHTML', false, `<span style="font-size: ${size}px;"></span>`);
                } catch (e2) {
                  // Last resort: apply to editor itself
                  editorRef.current.style.fontSize = `${size}px`;
                }
              }
            }
          }
        }
      }
      
      updateContent();
    }
  };

  const toolbarButtons = [
    {
      icon: <UndoIcon />,
      tooltip: 'Undo (Ctrl+Z)',
      onClick: undo,
      disabled: historyIndex <= 0
    },
    {
      icon: <RedoIcon />,
      tooltip: 'Redo (Ctrl+Y)',
      onClick: redo,
      disabled: historyIndex >= history.length - 1
    },
    { icon: <BoldIcon />, tooltip: 'Bold (Ctrl+B)', onClick: () => executeCommand('bold') },
    { icon: <ItalicIcon />, tooltip: 'Italic (Ctrl+I)', onClick: () => executeCommand('italic') },
    { icon: <UnderlineIcon />, tooltip: 'Underline (Ctrl+U)', onClick: () => executeCommand('underline') },
    { icon: <BulletListIcon />, tooltip: 'Bullet List', onClick: () => executeCommand('insertUnorderedList') },
    { icon: <NumberedListIcon />, tooltip: 'Numbered List', onClick: () => executeCommand('insertOrderedList') },
    { icon: <AlignLeftIcon />, tooltip: 'Align Left', onClick: () => executeCommand('justifyLeft') },
    { icon: <AlignCenterIcon />, tooltip: 'Align Center', onClick: () => executeCommand('justifyCenter') },
    { icon: <AlignRightIcon />, tooltip: 'Align Right', onClick: () => executeCommand('justifyRight') },
    { icon: <AlignJustifyIcon />, tooltip: 'Justify', onClick: () => executeCommand('justifyFull') }
  ];

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Box
        sx={{
          border: error ? '2px solid #d32f2f' : '1px solid #c4c4c4',
          borderRadius: 1,
          '&:hover': {
            borderColor: error ? '#d32f2f' : '#1976d2'
          },
          '&:focus-within': {
            borderColor: error ? '#d32f2f' : '#1976d2',
            borderWidth: 2
          }
        }}
      >
        {/* Toolbar */}
        <Toolbar
          variant="dense"
          sx={{
            minHeight: 40,
            borderBottom: '1px solid #e0e0e0',
            bgcolor: '#f5f5f5',
            flexWrap: 'wrap',
            gap: 0.5
          }}
        >
          {toolbarButtons.map((button, index) => (
            <Tooltip key={index} title={button.tooltip}>
              <span>
                <IconButton
                  size="small"
                  onClick={button.onClick}
                  disabled={button.disabled || disabled}
                  sx={{
                    color: button.disabled ? 'text.disabled' : 'text.primary',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  {button.icon}
                </IconButton>
              </span>
            </Tooltip>
          ))}
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>Font Size</InputLabel>
            <Select
              value={currentFontSize}
              onChange={handleFontSizeChange}
              label="Font Size"
              disabled={disabled}
            >
              <MenuItem value="10">10px</MenuItem>
              <MenuItem value="12">12px</MenuItem>
              <MenuItem value="14">14px</MenuItem>
              <MenuItem value="16">16px</MenuItem>
              <MenuItem value="18">18px</MenuItem>
              <MenuItem value="20">20px</MenuItem>
              <MenuItem value="24">24px</MenuItem>
              <MenuItem value="28">28px</MenuItem>
              <MenuItem value="32">32px</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>

        {/* Editor */}
        <Box
          ref={editorRef}
          contentEditable={!disabled}
          onInput={debouncedUpdateContent}
          onKeyDown={handleKeyDown}
          sx={{
            minHeight: rows * 24 + 16, // Approximate height based on rows
            padding: 2,
            outline: 'none',
            '&:empty:before': {
              content: `"${placeholder}"`,
              color: 'text.disabled',
              fontStyle: 'italic'
            },
            '& p': {
              margin: '0 0 8px 0',
              '&:last-child': {
                marginBottom: 0
              }
            },
            '& ul, & ol': {
              margin: '0 0 8px 0',
              paddingLeft: '20px'
            },
            '& li': {
              marginBottom: '4px'
            }
          }}
        />
      </Box>
      
      {helperText && (
        <Box sx={{ mt: 0.5, px: 2 }}>
          <TextField
            helperText={helperText}
            error={error}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{ '& .MuiFormHelperText-root': { mx: 0 } }}
          />
        </Box>
      )}
    </Box>
  );
};

export default RichTextEditor;
